/**
 * Unified catalog layer — merges Bokun products with Mostrando México's
 * own `custom_tours`. Frontend consumes `UnifiedTour` and never needs to
 * branch on origin for display.
 *
 * Slug strategy:
 *   - Bokun: existing `{slug}-{numericId}` (unchanged)
 *   - Custom: `mx-{slug}` (prefix guarantees no collision with Bokun ids)
 */

import { canonicalSlugForId } from "./canonical-tour-slugs";
import { toCatalogLite } from "./catalog-lite";
import { normalizeSpanishTitle, needsManualSpanishTitle } from "./catalog-presentation";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { RETIRED_TOUR_IDS } from "./retired-tours";


import { listProducts, getProduct, fetchBokunProduct } from "./bokun.functions";
import type { BokunProductSummary, Json } from "./bokun.functions";

export type TourSource = "bokun" | "custom";

/**
 * Lean listing shape shipped to card/grid views. Same key names as
 * `UnifiedTour` so components that consume both keep working, but heavy
 * fields (`description` HTML, extra images) are stripped to cut SSR
 * payload size dramatically.
 */
export interface UnifiedTourLite {
  cardCancellationPolicy?: string | null;
  pricingUnit?: "person" | "group" | "vessel" | null;
  needsManualTranslation?: boolean;
  source: TourSource;
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  description: null;
  images: string[]; // at most 1 element — the primary card image
  priceFrom: number | null;
  currency: string | null;
  durationMinutes: number | null;
  location: string | null;
  categories: string[];
  available: boolean;
  featured: boolean;
  vendorTitle: string | null;
}

export interface UnifiedTour {
  cardCancellationPolicy?: string | null;
  pricingUnit?: "person" | "group" | "vessel" | null;
  needsManualTranslation?: boolean;
  source: TourSource;
  /** Stable id per source: bokun product id, or `mx-{uuid}` for custom. */
  id: string;
  /** Public route slug. Custom slugs are prefixed with `mx-`. */
  slug: string;
  title: string;
  summary: string | null;
  description: string | null;
  images: string[];
  priceFrom: number | null;
  currency: string | null;
  durationMinutes: number | null;
  location: string | null;
  categories: string[];
  available: boolean;
  featured: boolean;
  vendorTitle: string | null;
}

/**
 * Bokun product IDs the client has chosen to hide from listings and detail pages.
 * Keep as strings; Bokun IDs flow through the catalog as strings.
 *
 * Includes the retired products (see `retired-tours.ts`): hidden everywhere,
 * but their URLs 301-redirect instead of 404ing.
 */
const HIDDEN_BOKUN_IDS = new Set<string>([...RETIRED_TOUR_IDS]);



/**
 * Bokun product IDs to force-include in the catalog even if Bokun's
 * `/activity.json/search` (forSale=true) omits them. Useful when a product
 * is set to `published: false` in Bokun but the operator wants it live on
 * Mostrando México. Each id is fetched individually via `getProduct`.
 *
 * NOTE: 995305 was removed on 2026-07-10 because Bokun returns 404 for it
 * (product no longer exists in Bokun's catalog) and each SSR was retrying
 * the fetch, adding ~2s of latency per page load.
 */
const FORCE_INCLUDE_BOKUN_IDS: string[] = [];


/**
 * Bokun product IDs whose source-of-truth language must always be Spanish.
 * For these, we ignore Bokun's per-language responses (they may be missing or
 * untranslated) and always fetch in `es`, then run our AI translation cache
 * to render the active language.
 */
const FORCE_ES_SOURCE_IDS = new Set<string>(["603407"]);

/**
 * El país NUNCA se deduce por palabras del título o la ubicación (causó
 * falsos positivos: "San Juan Teotihuacán", "Kukulcán"). La fuente de
 * verdad es lo que Bókun entrega por el canal autorizado + la lista interna
 * de exclusión (HIDDEN_BOKUN_IDS). Un país estructurado distinto de México
 * solo genera una advertencia; jamás oculta el producto.
 */
function warnIfStructuredCountryNotMexico(id: unknown, country: unknown): void {
  if (typeof country !== "string" || !country.trim()) return;
  const c = country.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (c === "mx" || c === "mex" || c === "mexico") return;
  console.warn("[catalog] país estructurado distinto de México (no se oculta)", { id, country });
}

export interface UnifiedTourDetail extends UnifiedTour {
  included: string[];
  excluded: string[];
  highlights: string[];
  recommendations: string | null;
  cancellationPolicy: string | null;
  /** Horas de cancelación gratuita según Bókun (null si no hay). */
  freeCancellationHours?: number | null;
  videoUrl: string | null;
  // Bokun-rich fields (null for custom tours)
  durationText: string | null;
  difficultyLevel: string | null;
  minAge: number | null;
  itinerary: { index: number; title: string; body: string | null }[];
  requirements: string | null;
  importantInfo: string | null;
  meetingType: string | null;
  startPoints: {
    id: string; title: string; address: string | null; city: string | null;
    latitude: number | null; longitude: number | null;
  }[];
  startTimes: { id: string; hour: number; minute: number; label: string | null }[];
  vendor: { id: string; title: string | null; email: string | null; phone: string | null; website: string | null } | null;
  bokunBookingUrl: string | null;
  bokunActivityId: string | null;
  openingHours: Json;
  pricingCategories: { id: string; title: string; minAge: number | null; maxAge: number | null; ticketCategory: string | null; groupSize?: number | null }[];
  extras: { id: string; title: string; description: string | null; price: number | null; currency: string | null }[];
  pickupOptions: { id: string; title: string; address: string | null }[];
  // Extended Bokun fields (null/empty for custom tours)
  guideLanguages: string[];
  audioLanguages: string[];
  subtitleLanguages: string[];
  restrictions: string | null;
  dressCode: string | null;
  physicalCondition: string | null;
  accessibility: string | null;
  voucherInfo: { type: string | null; redemptionMethod: string | null; printed: boolean | null } | null;
  bookingCutoffHours: number | null;
  minBookable: number | null;
  maxBookable: number | null;
  productGroups: string[];
  photos: { url: string; alt: string | null; caption: string | null }[];
  contact: { email: string | null; phone: string | null; website: string | null } | null;
  externalUrl: string | null;
  keywords: string[];
  productCode: string | null;
  rating: number | null;
  reviewCount: number | null;
  instantConfirmation: boolean | null;
  bookingType: string | null;
  capacityType: string | null;
  ticketPerPerson: boolean | null;
  // --- Own-tour (custom) extras. Undefined for Bokun products. ---
  changePolicy?: string | null;
  refundPolicy?: string | null;
  terms?: string | null;
  childrenNotes?: string | null;
  groupNotes?: string | null;
  maxAge?: number | null;
  requiresHotelInfo?: boolean;
  requiresFlightInfo?: boolean;
  /** Bookable add-ons with the pricing rules the server enforces. */
  bookingExtras?: {
    id: string; title: string; description: string | null; price: number;
    currency: string; chargeType: string; required: boolean; maxQuantity: number | null;
  }[];
  /** Questions the traveller must answer before paying. */
  bookingQuestions?: {
    id: string; label: string; helpText: string | null; fieldType: string;
    options: string[]; required: boolean; scope: string;
  }[];
  /** Meeting points / pickups, with any surcharge. */
  meetingPoints?: {
    id: string; name: string; address: string | null; kind: string;
    extraPrice: number; lat: number | null; lng: number | null;
    pickupOffsetMinutes: number | null;
  }[];
}

async function resolveTourImageUrl(sbAdmin: any, value: string): Promise<string> {
  if (!value.startsWith("tour-media:")) return value;
  const path = value.slice("tour-media:".length);
  const { data, error } = await sbAdmin.storage.from("tour-media").createSignedUrl(path, 60 * 60);
  if (error || !data?.signedUrl) return "";
  return data.signedUrl as string;
}

// --- Mappers ---------------------------------------------------------------

function slugifyTitle(title: string): string {
  return title
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function fromBokunSummary(p: BokunProductSummary): UnifiedTour {
  // Build SEO-friendly slug "title-id" when Bokun doesn't supply one.
  // Pinned products keep one canonical slug in every language.
  const seoSlug = canonicalSlugForId(p.id) ?? (p.slug && p.slug !== p.id
    ? p.slug
    : p.id
      ? `${slugifyTitle(p.title)}-${p.id}`
      : p.id);
  return {
    source: "bokun",
    cardCancellationPolicy: p.cardCancellationPolicy,
    pricingUnit: p.pricingUnit,
    id: p.id,
    slug: seoSlug,
    title: p.title,
    summary: p.summary,
    description: p.description,
    images: p.images,
    priceFrom: p.priceFrom,
    currency: p.currency ?? "MXN",
    durationMinutes: p.durationMinutes,
    location: p.location,
    categories: p.categories,
    available: p.available,
    featured: false,
    vendorTitle: p.vendorTitle,
  };
}

/** Normalize a vendor title for comparison (lowercase, strip accents & spaces). */
function normalizeVendor(s: string | null | undefined): string {
  return (s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

const MM_VENDOR_KEY = "mostrandomexico";

/** Vendor keys (normalized) to fully hide from listings and detail pages. */
const HIDDEN_VENDOR_KEYS = new Set<string>([
  // All previously hidden vendors have been reactivated by request.
]);


// --- Server functions ------------------------------------------------------

/**
 * Returns the merged catalog. Custom tours come first (featured-aware),
 * Bokun follows. Failures in either source degrade gracefully so the
 * site keeps working if one provider is down.
 */
export const listUnifiedCatalog = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({ lang: z.string().min(2).max(5).optional() }).optional(),
  )
  .handler(
  async ({ data }): Promise<{ tours: UnifiedTour[]; counts: { bokun: number; custom: number } }> => {
    const lang = (data?.lang ?? "es").slice(0, 2).toLowerCase();
    const { ttlSwr } = await import("./catalog-cache.server");
    const res = await ttlSwr(`catalog:${lang}`, () => buildUnifiedCatalog(lang), {
      // A truncated Bokun page-walk must never become last-known-good.
      acceptable: (v) => !v.partial,
      persistent: true,
      coldTimeoutMs: 12_000,
    });
    return pinSlugs(res, lang);
  },
);

/**
 * Uncached builder for the unified catalog. Extracted so `ttlSwr` can wrap
 * it while keeping the exported server-fn stable.
 */
export async function buildUnifiedCatalog(
  lang: string,
): Promise<{
  tours: UnifiedTour[];
  counts: { bokun: number; custom: number };
  partial: boolean;
}> {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");


    const sb = supabaseAdmin as any;
    // ALWAYS fetch Bokun in Spanish as the base source-of-truth. Non-Spanish
    // display languages are produced by our translation cache below. That
    // avoids Bokun's own English fallback leaking into the Spanish site when
    // a product's base language in Bokun is English.
    const [bokunResult, customResult] = await Promise.allSettled([
      listProducts({ data: { lang: "es" } }),
      sb
        .from("custom_tours")
        .select(
          "id,slug,title,short_description,description,city,state,country,duration_minutes,categories,featured,active,deleted_at,custom_tour_images(url,position),custom_tour_pricing(base_price,currency,tier)",
        )
        .eq("active", true)
        .is("deleted_at", null)
        .order("featured", { ascending: false })
        .order("created_at", { ascending: false }),
    ]);

    let bokunTours: UnifiedTour[] =
      bokunResult.status === "fulfilled"
        ? bokunResult.value.products
            .filter((p) => !HIDDEN_BOKUN_IDS.has(p.id))
            .filter((p) => !HIDDEN_VENDOR_KEYS.has(normalizeVendor(p.vendorTitle)))
            .map(fromBokunSummary)
        : [];

    // Force-include products that don't appear in Bokun's search results
    // (e.g. published=false). Fetch each individually and merge in.
    const presentIds = new Set(bokunTours.map((t) => t.id));
    const missingForced = FORCE_INCLUDE_BOKUN_IDS.filter((id) => !presentIds.has(id));
    if (missingForced.length) {
      const forced = await Promise.all(
        missingForced.map(async (id) => {
          try {
            const r = await fetchBokunProduct(id, "es");
            return fromBokunSummary(r.product);
          } catch (e) {
            console.warn("[catalog] force-include failed", id, e);
            return null;
          }
        }),
      );
      for (const t of forced) {
        if (t && !HIDDEN_BOKUN_IDS.has(t.id)) bokunTours.push(t);
      }
    }
    // Silence unused-const warning while keeping the intent documented.
    void FORCE_ES_SOURCE_IDS;

    let customTours: UnifiedTour[] = [];
    if (customResult.status === "fulfilled" && customResult.value.data) {
      customTours = await Promise.all(customResult.value.data.map(async (row: any) => {
        const images = await Promise.all((row.custom_tour_images ?? [])
          .slice()
          .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
          .map((img: any) => resolveTourImageUrl(sb, img.url as string)));

        const adultPrices = (row.custom_tour_pricing ?? [])
          .filter((pr: any) => pr.tier === "adult")
          .map((pr: any) => Number(pr.base_price))
          .filter((n: number) => Number.isFinite(n));
        const priceFrom = adultPrices.length ? Math.min(...adultPrices) : null;
        const currency =
          (row.custom_tour_pricing ?? []).find((pr: any) => pr.tier === "adult")?.currency ??
          "MXN";

        warnIfStructuredCountryNotMexico(row.id, row.country);
        const location = [row.city, row.state].filter(Boolean).join(", ") || row.country || null;

        return {
          source: "custom",
          id: `mx-${row.id}`,
          slug: `mx-${row.slug}`,
          title: row.title,
          summary: row.short_description ?? null,
          description: row.description ?? null,
          images,
          priceFrom,
          currency,
          durationMinutes: row.duration_minutes ?? null,
          location,
          categories: Array.isArray(row.categories)
            ? (row.categories as string[]).filter((c) => typeof c === "string" && c.trim())
            : [],
          available: true,
          featured: !!row.featured,
          vendorTitle: "Mostrando México",
        } satisfies UnifiedTour;
      }));
    }

    const bokunToursFiltered = bokunTours;

    // Translate user-visible fields to the requested language. Runs even
    // for `es` because Bokun/custom data may contain non-Spanish fallback
    // strings that must be normalised to the active language.
    try {
      const { translateProducts } = await import("./translate.server");
      await Promise.all([
        translateProducts(bokunToursFiltered, lang),
        translateProducts(customTours, lang),
      ]);
    } catch (e) {
      console.warn("[catalog] translation failed", e);
    }

    const featuredCustom = customTours.filter((t) => t.featured);
    const restCustom = customTours.filter((t) => !t.featured);
    const mmBokun = bokunToursFiltered.filter(
      (t) => normalizeVendor(t.vendorTitle) === MM_VENDOR_KEY,
    );
    const otherBokun = bokunToursFiltered.filter(
      (t) => normalizeVendor(t.vendorTitle) !== MM_VENDOR_KEY,
    );
    const merged = [...featuredCustom, ...mmBokun, ...restCustom, ...otherBokun];

    // Global dedup by stable id (Bokun product id or `mx-{uuid}`). Defensive:
    // protects against duplicates from upstream feeds, retried fetches, or
    // re-ordering between sources.
    const seen = new Set<string>();
    const tours: UnifiedTour[] = [];
    let dropped = 0;
    for (const t of merged) {
      if (!t?.id || seen.has(t.id)) { dropped += 1; continue; }
      seen.add(t.id);
      tours.push(t);
    }
    if (dropped > 0) {
      console.warn(`[catalog] dedup removed ${dropped} duplicate tour(s) from unified list`);
    }

    return {
      // Incomplete when Bokun failed outright or its pagination was cut short.
      partial: bokunResult.status !== "fulfilled" || bokunResult.value.partial === true,
      tours,
      counts: { bokun: bokunToursFiltered.length, custom: customTours.length },
    };
}

/**
 * Lite listing: same result as `listUnifiedCatalog` but trims heavy fields
 * before serialisation. Used by the home page, `InlineTourSearch` and any
 * other card-only surface. Cuts the SSR JSON payload for `/` from
 * ~345 KB to ~90 KB and shrinks the hydration script accordingly.
 */
export const listUnifiedCatalogLite = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({ lang: z.string().min(2).max(5).optional() }).optional(),
  )
  .handler(
    async ({ data }): Promise<{ tours: UnifiedTourLite[]; counts: { bokun: number; custom: number } }> => {
      const lang = (data?.lang ?? "es").slice(0, 2).toLowerCase();
      const { ttlSwr } = await import("./catalog-cache.server");
      return pinSlugs(await ttlSwr(`catalog-lite:${lang}`, async () => {
        const full = await buildUnifiedCatalog(lang);
        return toCatalogLite(full);
      }, { acceptable: (v) => !v.partial, persistent: true, coldTimeoutMs: 12_000 }), lang);
    },
  );

/** Apply pinned canonical slugs, also over cached/persisted catalog copies. */
async function pinSlugs<T extends { tours: { source: string; id: string; slug: string; title: string }[] }>(res: T, lang = "es"): Promise<T> {
  // import.meta.env.SSR keeps this server-only import out of the browser bundle.
  const overrides = import.meta.env.SSR && lang === "es"
    ? await (await import("./title-overrides.server")).getApprovedTitleOverrides()
    : new Map<string, string>();
  return {
    ...res,
    tours: res.tours.map((t) => {
      const pinned = t.source === "bokun" ? canonicalSlugForId(t.id) : null;
      const approved = t.source === "bokun" ? overrides.get(t.id) : undefined;
      const safePrice = t.source === "bokun" ? { priceFrom: null } : {};
      if (approved) return { ...t, ...safePrice, slug: pinned ?? t.slug, title: approved, needsManualTranslation: false };
      const title = normalizeSpanishTitle(t.title);
      return { ...t, ...safePrice, slug: pinned ?? t.slug, title, needsManualTranslation: needsManualSpanishTitle(title) };
    }),
  };
}



/**
 * Fetch a single tour by source + id (or slug). Returns the detail shape
 * with included/excluded/highlights and the rich metadata the detail page needs.
 */
export const getUnifiedTour = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      source: z.enum(["bokun", "custom"]),
      idOrSlug: z.string().min(1),
      lang: z.string().min(2).max(5).optional(),
    }),
  )
  .handler(async ({ data }): Promise<{ tour: UnifiedTourDetail | null }> => {
    const lang = (data.lang ?? "es").slice(0, 2).toLowerCase();
    // Always translate through our cache — the AI can auto-detect a
    // Bokun-fallback English string and translate it to `es` when the
    // active display language is Spanish. See translate.server.ts.
    const needsTranslation = true;

    if (data.source === "bokun") {
      if (HIDDEN_BOKUN_IDS.has(data.idOrSlug)) return { tour: null };
      // Always fetch Bokun in Spanish as base source-of-truth; our translator
      // normalises to the requested `lang` (including `es` when Bokun's own
      // response falls back to English).
      let res: Awaited<ReturnType<typeof getProduct>>;
      try {
        res = await getProduct({ data: { id: data.idOrSlug, lang: "es" } });
      } catch (e) {
        if ((e as Error)?.message === "Tour no encontrado.") return { tour: null };
        throw e;
      }
      const p = res.product;
      if (HIDDEN_BOKUN_IDS.has(p.id)) return { tour: null };
      if (HIDDEN_VENDOR_KEYS.has(normalizeVendor(p.vendorTitle))) return { tour: null };
      const policySummary = p.cancellationPolicy?.summary ?? null;
      const tour: { tour: UnifiedTourDetail | null } = {
        tour: {
          ...fromBokunSummary(p),
          included: p.included,
          excluded: p.excluded,
          highlights: p.highlights,
          recommendations: null,
          cancellationPolicy: policySummary,
          freeCancellationHours: p.freeCancellationHours ?? null,
          videoUrl: null,
          durationText: p.durationText,
          difficultyLevel: p.difficultyLevel,
          minAge: p.minAge,
          itinerary: p.itinerary,
          requirements: p.requirements,
          importantInfo: p.importantInfo,
          meetingType: p.meetingType,
          startPoints: p.startPoints,
          startTimes: p.startTimes,
          vendor: p.vendor,
          bokunBookingUrl: p.bokunBookingUrl,
          bokunActivityId: p.id,
          openingHours: (p as any).openingHours ?? null,
          pricingCategories: p.pricingCategories ?? [],
          extras: p.extras ?? [],
          pickupOptions: p.pickupOptions ?? [],
          guideLanguages: p.guideLanguages ?? [],
          audioLanguages: p.audioLanguages ?? [],
          subtitleLanguages: p.subtitleLanguages ?? [],
          restrictions: p.restrictions ?? null,
          dressCode: p.dressCode ?? null,
          physicalCondition: p.physicalCondition ?? null,
          accessibility: p.accessibility ?? null,
          voucherInfo: p.voucherInfo ?? null,
          bookingCutoffHours: p.bookingCutoffHours ?? null,
          minBookable: p.minBookable ?? null,
          maxBookable: p.maxBookable ?? null,
          productGroups: p.productGroups ?? [],
          photos: p.photos ?? [],
          contact: p.contact ?? null,
          externalUrl: p.externalUrl ?? null,
          keywords: p.keywords ?? [],
          productCode: p.productCode ?? null,
          rating: p.rating ?? null,
          reviewCount: p.reviewCount ?? null,
          instantConfirmation: p.instantConfirmation ?? null,
          bookingType: p.bookingType ?? null,
          capacityType: p.capacityType ?? null,
          ticketPerPerson: p.ticketPerPerson ?? null,
        },
      };
      if (needsTranslation && tour.tour && lang) {
        try {
          const { translateProductFields } = await import("./translate.server");
          await translateProductFields(tour.tour, lang);
        } catch (e) { console.warn("[catalog] tour translation failed", e); }
      }
      return tour;
    }



    // custom: idOrSlug may be uuid (without prefix) or slug (without `mx-`)
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const sb = supabaseAdmin as any;
    const key = data.idOrSlug.replace(/^mx-/, "");

    const selectCols =
      "id,slug,title,short_description,description,city,state,country,duration_minutes," +
      "included,excluded,highlights,recommendations,requirements,restrictions,terms," +
      "cancellation_policy,change_policy,refund_policy,children_notes,group_notes," +
      "categories,tags,languages,min_age,max_age,min_participants,max_participants," +
      "booking_cutoff_hours,instant_confirmation,requires_hotel_info,requires_flight_info," +
      "video_url,featured,active,deleted_at," +
      "custom_tour_images(url,position),custom_tour_pricing(base_price,currency,tier)," +
      "custom_tour_itinerary(id,title,description,position,duration_minutes,optional)," +
      "custom_tour_meeting_points(id,name,address,kind,extra_price,lat,lng,position,active,pickup_time_offset_minutes)," +
      "custom_tour_extras(id,title,description,price,currency,charge_type,required,max_quantity,position,active)," +
      "custom_tour_questions(id,label,help_text,field_type,options,required,scope,position,active)";

    const bySlug = await sb
      .from("custom_tours")
      .select(selectCols)
      .eq("active", true)
      .is("deleted_at", null)
      .eq("slug", key)
      .maybeSingle();
    let row: any = bySlug.data;
    if (!row && /^[0-9a-f-]{36}$/i.test(key)) {
      const byId = await sb
        .from("custom_tours")
        .select(selectCols)
        .eq("id", key)
        .eq("active", true)
        .is("deleted_at", null)
        .maybeSingle();
      row = byId.data;
    }

    if (!row) return { tour: null };

    const images = await Promise.all((row.custom_tour_images ?? [])
      .slice()
      .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
      .map((img: any) => resolveTourImageUrl(sb, img.url as string)));

    const adultPrices = (row.custom_tour_pricing ?? [])
      .filter((pr: any) => pr.tier === "adult")
      .map((pr: any) => Number(pr.base_price))
      .filter((n: number) => Number.isFinite(n));
    const priceFrom = adultPrices.length ? Math.min(...adultPrices) : null;
    const currency =
      (row.custom_tour_pricing ?? []).find((pr: any) => pr.tier === "adult")?.currency ?? "MXN";

    const location = [row.city, row.state].filter(Boolean).join(", ") || row.country || null;
    const included = Array.isArray(row.included) ? (row.included as string[]) : [];
    const excluded = Array.isArray(row.excluded) ? (row.excluded as string[]) : [];
    const strArr = (v: any): string[] => (Array.isArray(v) ? v.filter((x) => typeof x === "string" && x.trim()) : []);
    const byPos = (a: any, b: any) => (a.position ?? 0) - (b.position ?? 0);

    const itinerary = (row.custom_tour_itinerary ?? [])
      .slice()
      .sort(byPos)
      .map((step: any, i: number) => ({
        index: i + 1,
        title: step.title as string,
        body: (step.description as string | null) ?? null,
      }));

    const meetingPoints = (row.custom_tour_meeting_points ?? [])
      .filter((mp: any) => mp.active !== false)
      .slice()
      .sort(byPos)
      .map((mp: any) => ({
        id: mp.id as string,
        name: mp.name as string,
        address: (mp.address as string | null) ?? null,
        kind: (mp.kind as string) ?? "meeting_point",
        extraPrice: Number(mp.extra_price ?? 0) || 0,
        lat: mp.lat === null || mp.lat === undefined ? null : Number(mp.lat),
        lng: mp.lng === null || mp.lng === undefined ? null : Number(mp.lng),
        pickupOffsetMinutes:
          mp.pickup_time_offset_minutes === null || mp.pickup_time_offset_minutes === undefined
            ? null
            : Number(mp.pickup_time_offset_minutes),
      }));

    const bookingExtras = (row.custom_tour_extras ?? [])
      .filter((ex: any) => ex.active !== false)
      .slice()
      .sort(byPos)
      .map((ex: any) => ({
        id: ex.id as string,
        title: ex.title as string,
        description: (ex.description as string | null) ?? null,
        price: Number(ex.price ?? 0) || 0,
        currency: (ex.currency as string) ?? currency,
        chargeType: (ex.charge_type as string) ?? "per_booking",
        required: !!ex.required,
        maxQuantity:
          ex.max_quantity === null || ex.max_quantity === undefined ? null : Number(ex.max_quantity),
      }));

    const bookingQuestions = (row.custom_tour_questions ?? [])
      .filter((q: any) => q.active !== false)
      .slice()
      .sort(byPos)
      .map((q: any) => ({
        id: q.id as string,
        label: q.label as string,
        helpText: (q.help_text as string | null) ?? null,
        fieldType: (q.field_type as string) ?? "text",
        options: strArr(q.options),
        required: !!q.required,
        scope: (q.scope as string) ?? "booking",
      }));

    const customResult: { tour: UnifiedTourDetail | null } = {
      tour: {
        source: "custom",
        id: `mx-${row.id}`,
        slug: `mx-${row.slug}`,
        title: row.title,
        summary: row.short_description ?? null,
        description: row.description ?? null,
        images,
        priceFrom,
        currency,
        durationMinutes: row.duration_minutes ?? null,
        location,
        categories: strArr(row.categories),
        available: true,
        featured: !!row.featured,
        vendorTitle: "Mostrando México",
        included,
        excluded,
        highlights: strArr(row.highlights),
        recommendations: row.recommendations ?? null,
        cancellationPolicy: row.cancellation_policy ?? null,
        videoUrl: row.video_url ?? null,
        durationText: null,
        difficultyLevel: null,
        minAge: row.min_age ?? null,
        itinerary,
        requirements: row.requirements ?? null,
        importantInfo: null,
        meetingType: meetingPoints.some((mp: any) => mp.kind === "pickup") ? "PICK_UP" : null,
        startPoints: meetingPoints.map((mp: any) => ({
          id: mp.id,
          title: mp.name,
          address: mp.address,
          city: row.city ?? null,
          latitude: mp.lat,
          longitude: mp.lng,
        })),
        startTimes: [],
        vendor: null,
        bokunBookingUrl: null,
        bokunActivityId: null,
        openingHours: null,
        pricingCategories: [],
        extras: bookingExtras.map((ex: any) => ({
          id: ex.id,
          title: ex.title,
          description: ex.description,
          price: ex.price,
          currency: ex.currency,
        })),
        pickupOptions: meetingPoints
          .filter((mp: any) => mp.kind === "pickup")
          .map((mp: any) => ({ id: mp.id, title: mp.name, address: mp.address })),
        guideLanguages: strArr(row.languages),
        audioLanguages: [],
        subtitleLanguages: [],
        restrictions: row.restrictions ?? null,
        dressCode: null,
        physicalCondition: null,
        accessibility: null,
        voucherInfo: null,
        bookingCutoffHours: row.booking_cutoff_hours ?? null,
        minBookable: row.min_participants ?? null,
        maxBookable: row.max_participants ?? null,
        productGroups: [],
        photos: [],
        contact: null,
        externalUrl: null,
        keywords: strArr(row.tags),
        productCode: null,
        rating: null,
        reviewCount: null,
        instantConfirmation: row.instant_confirmation ?? null,
        bookingType: null,
        capacityType: null,
        ticketPerPerson: null,
        changePolicy: row.change_policy ?? null,
        refundPolicy: row.refund_policy ?? null,
        terms: row.terms ?? null,
        childrenNotes: row.children_notes ?? null,
        groupNotes: row.group_notes ?? null,
        maxAge: row.max_age ?? null,
        requiresHotelInfo: !!row.requires_hotel_info,
        requiresFlightInfo: !!row.requires_flight_info,
        bookingExtras,
        bookingQuestions,
        meetingPoints,
      },
    };
    if (needsTranslation && customResult.tour && lang) {
      try {
        const { translateProductFields } = await import("./translate.server");
        await translateProductFields(customResult.tour, lang);
      } catch (e) { console.warn("[catalog] custom tour translation failed", e); }
    }
    return customResult;
  });

/** Resolve a slug coming from `/tour/$slug` to a source + lookup key. */
export function parseUnifiedSlug(slug: string): {
  source: TourSource;
  key: string;
} {
  if (slug.startsWith("mx-")) return { source: "custom", key: slug.slice(3) };
  // Bokun: trailing numeric id, e.g. "snorkel-en-cozumel-12345"
  const m = slug.match(/-(\d+)$/);
  if (m) return { source: "bokun", key: m[1] };
  if (/^\d+$/.test(slug)) return { source: "bokun", key: slug };
  // Fallback: treat as custom slug
  return { source: "custom", key: slug };
}
